import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import { formatGeneratedAt } from "@/lib/pdf";
import logo from "@/logo.png";

import {
  formatReportRp,
  type RiderSaleReportData,
} from "./rider-sale-report-utils";

const ORANGE = "#E0702E";
const ORANGE_LIGHT = "#FBEAE0";
const DARK = "#1a1a1a";
const GRAY = "#555555";
const LIGHT_GRAY = "#888888";
const BORDER_COLOR = "#d1d5db";
const ROW_BORDER = "#e5e7eb";

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 40,
    paddingHorizontal: 30,
    fontSize: 8,
    fontFamily: "Helvetica",
    color: DARK,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  logo: {
    width: 38,
    height: 38,
    objectFit: "contain",
    marginRight: 10,
  },
  headerTextCol: {
    flexDirection: "column",
  },
  title: {
    fontFamily: "Helvetica-Bold",
    fontSize: 16,
    color: ORANGE,
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 9,
    color: GRAY,
  },
  summarySection: {
    marginBottom: 12,
  },
  summaryLine: {
    fontSize: 8.5,
    color: DARK,
    lineHeight: 1.35,
  },
  sectionTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9.5,
    color: DARK,
    marginTop: 10,
    marginBottom: 4,
  },
  // Main summary table
  summaryTable: {
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    marginBottom: 8,
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: ORANGE_LIGHT,
    borderBottomWidth: 1,
    borderBottomColor: BORDER_COLOR,
    alignItems: "center",
  },
  tableHeaderCell: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    paddingVertical: 4,
    paddingHorizontal: 4,
    color: DARK,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: ROW_BORDER,
    alignItems: "center",
  },
  tableCell: {
    fontSize: 7.5,
    paddingVertical: 3,
    paddingHorizontal: 4,
    color: DARK,
  },
  tableCellBold: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7.5,
    paddingVertical: 4,
    paddingHorizontal: 4,
    color: DARK,
  },
  tableTotalRow: {
    flexDirection: "row",
    borderTopWidth: 1.5,
    borderTopColor: BORDER_COLOR,
    backgroundColor: "#fafafa",
    alignItems: "center",
  },
  // Rekap tables
  rekapTable: {
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    marginBottom: 8,
  },
  notesBox: {
    marginVertical: 4,
    paddingLeft: 4,
  },
  noteItem: {
    fontSize: 7.5,
    color: "#b45309",
    fontFamily: "Helvetica",
    marginBottom: 2,
  },
  extraNote: {
    fontSize: 7.5,
    color: GRAY,
    fontStyle: "italic",
    marginTop: 2,
    marginBottom: 6,
  },
  // Detail page cards
  cardsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  riderCard: {
    width: "48.5%",
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    borderRadius: 3,
    padding: 6,
    marginBottom: 10,
    backgroundColor: "#ffffff",
  },
  cardRiderName: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9.5,
    color: DARK,
    marginBottom: 2,
  },
  cardMeta: {
    fontSize: 7,
    color: GRAY,
    lineHeight: 1.25,
  },
  cardTable: {
    marginTop: 5,
    borderWidth: 0.5,
    borderColor: BORDER_COLOR,
  },
  cardTableHeader: {
    flexDirection: "row",
    backgroundColor: ORANGE_LIGHT,
    borderBottomWidth: 0.5,
    borderBottomColor: BORDER_COLOR,
    alignItems: "center",
  },
  cardHeaderCell: {
    fontFamily: "Helvetica-Bold",
    fontSize: 6.5,
    paddingVertical: 2,
    paddingHorizontal: 2,
    color: DARK,
    textAlign: "right",
  },
  cardTableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: ROW_BORDER,
    alignItems: "center",
  },
  cardCell: {
    fontSize: 6.5,
    paddingVertical: 2,
    paddingHorizontal: 2,
    color: DARK,
    textAlign: "right",
  },
  cardTableTotalRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: BORDER_COLOR,
    backgroundColor: "#f9f9f9",
    alignItems: "center",
  },
  cardCellBold: {
    fontFamily: "Helvetica-Bold",
    fontSize: 6.5,
    paddingVertical: 2,
    paddingHorizontal: 2,
    color: DARK,
    textAlign: "right",
  },
  // Footer: absolute positioning inside page bottom so it stays pinned and never overlaps content
  footer: {
    position: "absolute",
    bottom: 14,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 7,
    color: LIGHT_GRAY,
  },
});

export interface RiderSaleReportPdfDocumentProps {
  data: RiderSaleReportData;
}

export function RiderSaleReportPdfDocument({
  data,
}: RiderSaleReportPdfDocumentProps) {
  const hasOther = data.hasOtherPayment;
  const isMultiDay = data.isMultiDay;
  const generatedAt = formatGeneratedAt();

  // Width percentages for Rider Summary Table
  const wHari = "6%";
  const wRider = isMultiDay
    ? hasOther
      ? "18%"
      : "22%"
    : hasOther
      ? "22%"
      : "26%";
  const wQty = hasOther ? "8%" : "9%";
  const wMoney = isMultiDay
    ? hasOther
      ? "12.5%"
      : "14%"
    : hasOther
      ? "13%"
      : "15%";
  const wSelisih = hasOther ? "10%" : "11%";

  const renderHeader = () => (
    <View style={styles.headerRow}>
      <Image src={logo} style={styles.logo} />
      <View style={styles.headerTextCol}>
        <Text style={styles.title}>Laporan Penjualan Rider</Text>
        <Text style={styles.subtitle}>
          {data.workspaceName}, {data.formattedPeriod}
        </Text>
      </View>
    </View>
  );

  const renderFooter = () => (
    <View style={styles.footer} fixed>
      <Text style={styles.footerText}>Dicetak pada {generatedAt}</Text>
      <Text
        style={styles.footerText}
        render={({ pageNumber, totalPages }) =>
          `Halaman ${pageNumber}/${totalPages} - Penjualan Rider ${data.formattedPeriod}`
        }
      />
    </View>
  );

  const docTitle = isMultiDay
    ? `Penjualan-Rider-${data.dateFrom}-${data.dateTo}`
    : `Penjualan-Rider-${data.dateFrom}`;

  return (
    <Document title={docTitle}>
      {/* PAGE 1: Summary & Rekap */}
      <Page size="A4" style={styles.page}>
        {renderHeader()}

        {/* Summary sentences */}
        <View style={styles.summarySection}>
          <Text style={[styles.summaryLine, { fontFamily: "Helvetica-Bold" }]}>
            Status: {data.status}
          </Text>
          <Text style={styles.summaryLine}>{data.sentenceOmzet}</Text>
          <Text style={styles.summaryLine}>{data.sentencePembayaran}</Text>
          {data.issueSummary && (
            <Text style={[styles.summaryLine, { color: "#b45309" }]}>
              {data.issueSummary}
            </Text>
          )}
        </View>

        {/* Rider Summary Table */}
        <View style={styles.summaryTable}>
          <View style={styles.tableHeaderRow}>
            <Text style={[styles.tableHeaderCell, { width: wRider }]}>
              Rider
            </Text>
            {isMultiDay && (
              <Text
                style={[
                  styles.tableHeaderCell,
                  { width: wHari, textAlign: "right" },
                ]}
              >
                Hari
              </Text>
            )}
            <Text
              style={[
                styles.tableHeaderCell,
                { width: wQty, textAlign: "right" },
              ]}
            >
              Jual
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: wQty, textAlign: "right" },
              ]}
            >
              Sisa
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: wMoney, textAlign: "right" },
              ]}
            >
              Omzet
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: wMoney, textAlign: "right" },
              ]}
            >
              QRIS
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: wMoney, textAlign: "right" },
              ]}
            >
              Tunai
            </Text>
            {hasOther && (
              <Text
                style={[
                  styles.tableHeaderCell,
                  { width: wMoney, textAlign: "right" },
                ]}
              >
                Lainnya
              </Text>
            )}
            <Text
              style={[
                styles.tableHeaderCell,
                { width: wSelisih, textAlign: "right" },
              ]}
            >
              Selisih
            </Text>
          </View>

          {data.riders.map((r) => (
            <View key={r.riderId || r.riderName} style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: wRider }]}>
                {r.riderName}
                {r.status === "draft" ? " (draft)" : ""}
              </Text>
              {isMultiDay && (
                <Text
                  style={[
                    styles.tableCell,
                    { width: wHari, textAlign: "right" },
                  ]}
                >
                  {r.workDaysCount}
                </Text>
              )}
              <Text
                style={[styles.tableCell, { width: wQty, textAlign: "right" }]}
              >
                {r.soldQty}
              </Text>
              <Text
                style={[styles.tableCell, { width: wQty, textAlign: "right" }]}
              >
                {r.remainingQty}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  { width: wMoney, textAlign: "right" },
                ]}
              >
                {formatReportRp(r.omzet)}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  { width: wMoney, textAlign: "right" },
                ]}
              >
                {formatReportRp(r.qris)}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  { width: wMoney, textAlign: "right" },
                ]}
              >
                {formatReportRp(r.cash)}
              </Text>
              {hasOther && (
                <Text
                  style={[
                    styles.tableCell,
                    { width: wMoney, textAlign: "right" },
                  ]}
                >
                  {formatReportRp(r.other)}
                </Text>
              )}
              <Text
                style={[
                  styles.tableCell,
                  {
                    width: wSelisih,
                    textAlign: "right",
                    color: r.selisih !== 0 ? "#b45309" : DARK,
                  },
                ]}
              >
                {formatReportRp(r.selisih)}
              </Text>
            </View>
          ))}

          {/* TOTAL Row */}
          <View style={styles.tableTotalRow}>
            <Text style={[styles.tableCellBold, { width: wRider }]}>TOTAL</Text>
            {isMultiDay && (
              <Text
                style={[
                  styles.tableCellBold,
                  { width: wHari, textAlign: "right" },
                ]}
              >
                -
              </Text>
            )}
            <Text
              style={[
                styles.tableCellBold,
                { width: wQty, textAlign: "right" },
              ]}
            >
              {data.totals.soldQty}
            </Text>
            <Text
              style={[
                styles.tableCellBold,
                { width: wQty, textAlign: "right" },
              ]}
            >
              {data.totals.remainingQty}
            </Text>
            <Text
              style={[
                styles.tableCellBold,
                { width: wMoney, textAlign: "right" },
              ]}
            >
              {formatReportRp(data.totals.omzet)}
            </Text>
            <Text
              style={[
                styles.tableCellBold,
                { width: wMoney, textAlign: "right" },
              ]}
            >
              {formatReportRp(data.totals.qris)}
            </Text>
            <Text
              style={[
                styles.tableCellBold,
                { width: wMoney, textAlign: "right" },
              ]}
            >
              {formatReportRp(data.totals.cash)}
            </Text>
            {hasOther && (
              <Text
                style={[
                  styles.tableCellBold,
                  { width: wMoney, textAlign: "right" },
                ]}
              >
                {formatReportRp(data.totals.other)}
              </Text>
            )}
            <Text
              style={[
                styles.tableCellBold,
                {
                  width: wSelisih,
                  textAlign: "right",
                  color: data.totals.selisih !== 0 ? "#b45309" : DARK,
                },
              ]}
            >
              {formatReportRp(data.totals.selisih)}
            </Text>
          </View>
        </View>

        {/* Discrepancy & Draft notes */}
        {data.discrepancyNotes.length > 0 && (
          <View style={styles.notesBox}>
            {data.discrepancyNotes.map((item, idx) => (
              <Text key={idx} style={styles.noteItem}>
                {item.riderName}: {item.note}
              </Text>
            ))}
          </View>
        )}

        {/* Rekap Stok Minuman & Bakery */}
        {data.rekapCategories.map((cat) => {
          const soldBreadItems =
            cat.category === "bakery"
              ? cat.items.filter((i) => i.terjual > 0)
              : [];

          return (
            <View key={cat.category}>
              <Text style={styles.sectionTitle}>{cat.title}</Text>
              <View style={styles.rekapTable}>
                <View style={styles.tableHeaderRow}>
                  <Text style={[styles.tableHeaderCell, { width: "40%" }]}>
                    Varian
                  </Text>
                  <Text
                    style={[
                      styles.tableHeaderCell,
                      { width: "15%", textAlign: "right" },
                    ]}
                  >
                    Ambil
                  </Text>
                  <Text
                    style={[
                      styles.tableHeaderCell,
                      { width: "15%", textAlign: "right" },
                    ]}
                  >
                    Terjual
                  </Text>
                  <Text
                    style={[
                      styles.tableHeaderCell,
                      { width: "15%", textAlign: "right" },
                    ]}
                  >
                    Sisa
                  </Text>
                  <Text
                    style={[
                      styles.tableHeaderCell,
                      { width: "15%", textAlign: "right" },
                    ]}
                  >
                    Omzet
                  </Text>
                </View>

                {cat.items.map((item) => (
                  <View key={item.productId} style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: "40%" }]}>
                      {item.productName}
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        { width: "15%", textAlign: "right" },
                      ]}
                    >
                      {item.diambil}
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        { width: "15%", textAlign: "right" },
                      ]}
                    >
                      {item.terjual}
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        { width: "15%", textAlign: "right" },
                      ]}
                    >
                      {item.sisa}
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        { width: "15%", textAlign: "right" },
                      ]}
                    >
                      {formatReportRp(item.omzet)}
                    </Text>
                  </View>
                ))}

                <View style={styles.tableTotalRow}>
                  <Text style={[styles.tableCellBold, { width: "40%" }]}>
                    TOTAL
                  </Text>
                  <Text
                    style={[
                      styles.tableCellBold,
                      { width: "15%", textAlign: "right" },
                    ]}
                  >
                    {cat.totalDiambil}
                  </Text>
                  <Text
                    style={[
                      styles.tableCellBold,
                      { width: "15%", textAlign: "right" },
                    ]}
                  >
                    {cat.totalTerjual}
                  </Text>
                  <Text
                    style={[
                      styles.tableCellBold,
                      { width: "15%", textAlign: "right" },
                    ]}
                  >
                    {cat.totalSisa}
                  </Text>
                  <Text
                    style={[
                      styles.tableCellBold,
                      { width: "15%", textAlign: "right" },
                    ]}
                  >
                    {formatReportRp(cat.totalOmzet)}
                  </Text>
                </View>
              </View>

              {soldBreadItems.length > 0 && (
                <Text style={styles.extraNote}>
                  Rekap roti terjual:{" "}
                  {soldBreadItems
                    .map((b) => `${b.productName} ${b.terjual} pcs`)
                    .join(" dan ")}
                  .
                </Text>
              )}
            </View>
          );
        })}

        {renderFooter()}
      </Page>

      {/* PAGE 2+: Detail per rider (2x2 grid, up to 4 riders per page) */}
      {data.riderPages.map((pageRiders, pageIdx) => (
        <Page key={pageIdx} size="A4" style={styles.page}>
          {renderHeader()}

          <Text style={styles.sectionTitle}>
            Detail varian per rider (cup / pcs)
          </Text>

          <View style={styles.cardsGrid}>
            {pageRiders.map((r) => {
              const activeLines = r.lines.filter((l) => l.dispatchedQty > 0);
              const cardTotalDispatched = activeLines.reduce(
                (s, l) => s + l.dispatchedQty,
                0,
              );
              const cardTotalSold = activeLines.reduce(
                (s, l) => s + l.soldQty,
                0,
              );
              const cardTotalRemaining = activeLines.reduce(
                (s, l) => s + l.remainingQty,
                0,
              );

              return (
                <View key={r.riderId || r.riderName} style={styles.riderCard}>
                  <Text style={styles.cardRiderName}>
                    {r.riderName}
                    {r.status === "draft" ? " (draft)" : ""}
                  </Text>
                  <Text style={styles.cardMeta}>
                    Omzet {formatReportRp(r.omzet)}
                  </Text>
                  <Text style={styles.cardMeta}>
                    QRIS {formatReportRp(r.qris)}; tunai{" "}
                    {formatReportRp(r.cash)}
                    {r.other > 0 ? `; lainnya ${formatReportRp(r.other)}` : ""}
                  </Text>
                  <Text style={styles.cardMeta}>
                    Pembayaran {formatReportRp(r.pembayaran)}; selisih{" "}
                    {formatReportRp(r.selisih)}
                  </Text>

                  <View style={styles.cardTable}>
                    <View style={styles.cardTableHeader}>
                      <Text
                        style={[
                          styles.cardHeaderCell,
                          { width: "52%", textAlign: "left" },
                        ]}
                      >
                        Varian
                      </Text>
                      <Text style={[styles.cardHeaderCell, { width: "16%" }]}>
                        Ambil
                      </Text>
                      <Text style={[styles.cardHeaderCell, { width: "16%" }]}>
                        Jual
                      </Text>
                      <Text style={[styles.cardHeaderCell, { width: "16%" }]}>
                        Sisa
                      </Text>
                    </View>

                    {activeLines.map((line) => (
                      <View
                        key={line.productId || line.productName}
                        style={styles.cardTableRow}
                      >
                        <Text
                          style={[
                            styles.cardCell,
                            { width: "52%", textAlign: "left" },
                          ]}
                        >
                          {line.productName}
                        </Text>
                        <Text style={[styles.cardCell, { width: "16%" }]}>
                          {line.dispatchedQty}
                        </Text>
                        <Text style={[styles.cardCell, { width: "16%" }]}>
                          {line.soldQty}
                        </Text>
                        <Text style={[styles.cardCell, { width: "16%" }]}>
                          {line.remainingQty}
                        </Text>
                      </View>
                    ))}

                    <View style={styles.cardTableTotalRow}>
                      <Text
                        style={[
                          styles.cardCellBold,
                          { width: "52%", textAlign: "left" },
                        ]}
                      >
                        TOTAL
                      </Text>
                      <Text style={[styles.cardCellBold, { width: "16%" }]}>
                        {cardTotalDispatched}
                      </Text>
                      <Text style={[styles.cardCellBold, { width: "16%" }]}>
                        {cardTotalSold}
                      </Text>
                      <Text style={[styles.cardCellBold, { width: "16%" }]}>
                        {cardTotalRemaining}
                      </Text>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>

          {renderFooter()}
        </Page>
      ))}
    </Document>
  );
}
